import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { fetchPageContent, type PageSection } from '../../utils/pageContentStorage';
import { useSEO } from '@/hooks/useSEO';

const values = [
  { icon: 'ri-shield-check-line', title: '誠信透明', desc: '報價清楚，不誇大問題，修不好不收費' },
  { icon: 'ri-cpu-line', title: '主機板級技術', desc: '具備精密焊接與主板層級故障排除能力' },
  { icon: 'ri-time-line', title: '效率快速', desc: '多數維修當日完工，減少您的等待時間' },
  { icon: 'ri-global-line', title: '跨國維修', desc: '接受海外寄修，標準化作業確保穩定交付' },
];

const milestones = [
  { year: '2021', event: '兄弟手機維修正式成立，核心工程師帶入 15 年實務技術' },
  { year: '2022', event: '開始承接全台同業代工維修，主機板修復與精密焊接業務' },
  { year: '2023', event: '取得 Apple IRP 獨立維修中心認證，維修流程符合 Apple 標準規範' },
  { year: '2024', event: '累積破萬台設備維修，正式開放海外寄修跨國服務' },
];

const irpPoints = [
  { icon: 'ri-checkbox-circle-line', text: '維修流程符合 Apple 標準規範' },
  { icon: 'ri-tools-line', text: '可使用官方維修工具與診斷系統' },
  { icon: 'ri-award-line', text: '技術能力通過 Apple 官方審核' },
  { icon: 'ri-shield-check-line', text: '正品零件，完整保固保障' },
];

const DEFAULT_STORY = '兄弟手機維修成立於 2021 年。創立時間不長，但技術累積並不短。核心維修工程師擁有 15 年以上實務經驗，從早期功能型手機、智慧型手機初代機型，到現代高整合主機板架構，一路參與並熟悉硬體演進。我們的基礎不建立在話術，而建立在長期維修實戰。\n\n累積超過萬台以上設備維修經驗，涵蓋 iPhone 全系列、Samsung Galaxy、Google Pixel 及各大 Android 品牌，同時具備主機板級維修與精密焊接能力。大量案例的價值不在於數字，而在於對故障邏輯的判斷精準度——我們清楚哪些問題常見，也清楚哪些狀況不能草率處理。\n\n除門市服務外，亦承接全台同業代工維修，包含主機板層級故障排除、短路檢測、電源 IC 修復、進水腐蝕處理與高難度焊接作業。當一般維修單位無法處理時，會轉交由我們進行技術修復。這代表的不是規模，而是信任。同時，我們亦不定期接收海外寄修案件，跨國維修流程採標準化作業與完整測試，確保每一台設備穩定交付。';

export default function AboutPage() {
  useSEO({
    title: '關於兄弟維修｜Apple IRP 認證・15年維修技術',
    description: '兄弟維修成立於 2021 年，核心工程師擁有 15 年以上實務經驗，取得 Apple IRP 獨立維修中心認證。累積萬台以上設備維修，承接全台同業代工，提供海外寄修服務。',
    keywords: '兄弟維修,Apple IRP認證,手機維修台北,主機板維修,資料救援,萬華維修店',
    canonical: '/about',
  });

  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState<PageSection | null>(null);
  const [storyData, setStoryData] = useState<PageSection | null>(null);

  useEffect(() => {
    fetchPageContent().then(content => {
      setBannerData(content.find(s => s.id === 'about-banner') ?? null);
      setStoryData(content.find(s => s.id === 'about-story-v2') ?? null);
    });
  }, []);

  const bannerImage = bannerData?.image ?? 'https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/abouthero.jpg';
  const bannerTitle = bannerData?.title ?? '關於兄弟手機維修';
  const bannerSubtitle = bannerData?.subtitle ?? '成立於 2021 年，技術深度超越年份。實戰累積，不靠話術。';

  const rawStory = storyData?.content ?? DEFAULT_STORY;
  const storyParagraphs = rawStory.split(/\n\n+/).map(p => p.trim()).filter(Boolean);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative pt-32 pb-20 overflow-hidden bg-gray-950">
          <div className="absolute inset-0">
            <img src={bannerImage} alt="" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/70 to-gray-950/85" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-5">
              <span className="w-8 h-px bg-orange-400" />
              關於我們
              <span className="w-8 h-px bg-orange-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-5">{bannerTitle}</h1>
            <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">{bannerSubtitle}</p>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-4">
                  <span className="w-8 h-px bg-orange-500" />
                  我們的故事
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  技術不靠話術，<br />
                  <span className="text-orange-500">實戰才是根基</span>
                </h2>
                <div className="space-y-5 text-gray-600 leading-relaxed text-sm">
                  {storyParagraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div className="flex gap-10 mt-10">
                  {[
                    { num: '15+', label: '年核心技術積累' },
                    { num: '萬台+', label: '累積維修設備' },
                    { num: 'IRP', label: 'Apple 認證' },
                  ].map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold text-orange-500">{s.num}</div>
                      <div className="text-gray-500 text-xs mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-5">
                <div className="relative">
                  <img
                    src="https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/about-tech-img.jpg"
                    alt="主機板精密維修作業"
                    className="w-full h-64 object-cover object-top rounded-2xl"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-orange-500 text-white rounded-2xl px-5 py-3 text-center">
                    <div className="text-xl font-bold">2021</div>
                    <div className="text-xs opacity-90">正式創立</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="bg-gray-50 rounded-2xl p-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl mb-3">
                      <i className="ri-community-line text-orange-500 text-lg" />
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">同業代工維修</div>
                    <div className="text-xs text-gray-500 leading-relaxed">承接全台同業無法處理的高難度主機板修復，是業界信任的技術夥伴</div>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl mb-3">
                      <i className="ri-flight-takeoff-line text-orange-500 text-lg" />
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">海外寄修服務</div>
                    <div className="text-xs text-gray-500 leading-relaxed">標準化跨國維修流程，完整測試後穩定交付，遠在海外也能放心送修</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* IRP Certification */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
                  <i className="ri-apple-line text-sm" />
                  Apple Independent Repair Provider
                </div>
                <h2 className="text-3xl font-bold text-white mb-5">IRP 認證</h2>
                <p className="text-gray-400 leading-relaxed mb-8 text-sm">
                  兄弟手機維修取得 Apple IRP（Independent Repair Provider）蘋果獨立維修中心認證。這不只是資格，而是一套標準——代表我們以符合 Apple 官方規範的流程與工具，為每一台 Apple 設備提供有保障的維修服務。
                </p>
                <div className="space-y-3">
                  {irpPoints.map(pt => (
                    <div key={pt.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-orange-500/15 rounded-xl flex-shrink-0">
                        <i className={`${pt.icon} text-orange-400 text-sm`} />
                      </div>
                      <span className="text-gray-300 text-sm">{pt.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/irp-cert-img.jpg"
                  alt="Apple IRP 認證維修服務"
                  className="w-full h-80 object-cover object-top rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/40 to-transparent rounded-2xl" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className="ri-apple-line text-white text-lg" />
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">Apple IRP 認證中心</div>
                      <div className="text-gray-300 text-xs mt-0.5">Independent Repair Provider</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">我們的核心能力</h2>
              <p className="text-gray-500 text-sm">每項能力都來自長年實戰積累</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map(v => (
                <div key={v.title} className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100">
                  <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-2xl mx-auto mb-4">
                    <i className={`${v.icon} text-orange-500 text-2xl`} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">{v.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">成長歷程</h2>
              <p className="text-gray-500 text-sm">每一步都是實力的累積</p>
            </div>
            <div className="relative">
              <div className="absolute left-16 top-0 bottom-0 w-px bg-orange-100" />
              <div className="space-y-8">
                {milestones.map(m => (
                  <div key={m.year} className="flex items-start gap-8">
                    <div className="w-14 text-right flex-shrink-0">
                      <span className="text-orange-500 font-bold text-sm">{m.year}</span>
                    </div>
                    <div className="relative flex-shrink-0">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="w-3 h-3 bg-orange-500 rounded-full border-2 border-white ring-2 ring-orange-200" />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl px-5 py-3 flex-1 border border-gray-100">
                      <p className="text-gray-700 text-sm">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">技術說話，歡迎親自驗證</h2>
            <p className="text-orange-100 mb-8 text-sm">免費診斷・透明報價・當日取件・IRP 認證保障</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 bg-white text-orange-500 font-bold rounded-full cursor-pointer hover:bg-orange-50 transition-colors whitespace-nowrap">查詢維修報價</button>
              <button onClick={() => navigate('/contact')} className="px-8 py-3.5 border-2 border-white/60 text-white font-bold rounded-full cursor-pointer hover:border-white transition-colors whitespace-nowrap">立即聯絡</button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
